import React, { useState, useRef } from 'react';
import {
  ArrowLeft, Building2, MapPin, Upload,
  Loader2, Stethoscope, FileText, Send, CheckCircle, X, File, Image as ImageIcon
} from 'lucide-react';
import { useApplyForOpportunity } from '../hooks/use-opportunities';
import { uploadFile } from '../../../lib/uploads';
import { useMe } from '../../../hooks/useMe';

// ── File Drop Zone component ───────────────────────────────────────────────────
// `slot` is the upload state for this zone: { file, key, uploading, progress,
// failed }, or null when nothing is chosen. The file starts uploading as soon as
// it is picked, so the row shows its progress rather than waiting for submit.
const FileDropZone = ({ label, accept, icon: Icon, slot, onFileSelect, onClear, onError }) => {
  const inputRef = useRef(null);
  const file = slot?.file;

  return (
    <div>
      <div className="text-sm font-bold text-gray-900 mb-3">{label}</div>
      {file ? (
        <div
          className={`flex items-center gap-3 p-4 border rounded-xl ${
            slot.failed ? 'bg-red-50 border-red-200' : 'bg-indigo-50 border-indigo-200'
          }`}
        >
          {slot.uploading ? (
            <Loader2 size={20} className="text-indigo-500 flex-shrink-0 animate-spin" />
          ) : (
            <File size={20} className={`flex-shrink-0 ${slot.failed ? 'text-red-500' : 'text-indigo-500'}`} />
          )}
          <span
            className={`text-sm font-semibold flex-1 truncate ${
              slot.failed ? 'text-red-800' : 'text-indigo-800'
            }`}
          >
            {file.name}
            {slot.uploading && (
              <span className="font-normal text-indigo-500"> — uploading {slot.progress}%</span>
            )}
            {slot.failed && <span className="font-normal text-red-500"> — upload failed</span>}
          </span>
          <button
            type="button"
            onClick={onClear}
            className={`p-1 rounded-lg transition-colors ${
              slot.failed ? 'hover:bg-red-100' : 'hover:bg-indigo-100'
            }`}
          >
            <X size={14} className={slot.failed ? 'text-red-500' : 'text-indigo-500'} />
          </button>
        </div>
      ) : (
        <div
          onClick={() => inputRef.current?.click()}
          className="border-2 border-dashed border-gray-200 rounded-xl p-8 flex flex-col items-center justify-center text-center hover:bg-gray-50 hover:border-indigo-300 cursor-pointer transition-all duration-200"
        >
          <Icon size={24} className="text-gray-300 mb-3" />
          <p className="text-sm font-semibold text-gray-700 mb-1">Click to upload</p>
          <p className="text-xs text-gray-400">{accept}</p>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={accept}
        onChange={(e) => {
          const selectedFile = e.target.files?.[0];
          if (selectedFile) {
            // 1MB, matching the server and the "Up to 1MB" text already shown
            // beside this control — which the old 10MB check contradicted.
            if (selectedFile.size > 1 * 1024 * 1024) {
              if (onError) onError('File size must be under 1MB.');
              e.target.value = ''; // clear selection
              return;
            }
            onFileSelect(selectedFile);
          }
        }}
      />
    </div>
  );
};

// ── Info row skeleton ──────────────────────────────────────────────────────────
const InfoSkeleton = () => (
  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 animate-pulse">
    {[...Array(5)].map((_, i) => (
      <div key={i}>
        <div className="h-3 bg-gray-200 rounded mb-2 w-16" />
        <div className="h-4 bg-gray-200 rounded w-24" />
      </div>
    ))}
  </div>
);

// ── Main Component ─────────────────────────────────────────────────────────────
const ApplyModal = ({ isOpen, onClose, opportunity, onApplySuccess }) => {
  const [form, setForm] = useState({ screeningAnswer: '' });
  // Each slot holds { file, key, uploading, progress, failed }. Uploads start on
  // select and run straight to B2, so by the time the applicant presses Submit
  // the files are already stored and only their keys are sent.
  const [resume, setResume] = useState(null);
  const [caseResponse, setCaseResponse] = useState(null);
  const [error, setError] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);

  const uploading = !!resume?.uploading || !!caseResponse?.uploading;

  /**
   * Uploads one file for a slot, keeping that slot's state in step.
   *
   * A file that is removed after uploading is simply dropped: the object stays
   * in the staging prefix and the bucket's lifecycle rule clears it, so there is
   * no un-upload request to make.
   */
  const startUpload = async (file, purpose, setSlot) => {
    setSlot({ file, key: null, uploading: true, progress: 0, failed: false });
    setError('');
    try {
      const result = await uploadFile(file, {
        purpose,
        onProgress: (progress) =>
          setSlot((prev) => (prev?.file === file ? { ...prev, progress } : prev)),
      });
      setSlot((prev) =>
        prev?.file === file ? { ...prev, key: result.key, uploading: false, progress: 100 } : prev
      );
    } catch (err) {
      setSlot((prev) => (prev?.file === file ? { ...prev, uploading: false, failed: true } : prev));
      setError(err?.response?.data?.message || 'File upload failed. Please remove it and try again.');
    }
  };

  const MAX_LENGTH = 200;
  const isLongQuestion = opportunity?.screeningQuestion?.length > MAX_LENGTH;
  const displayQuestion = isExpanded || !isLongQuestion 
    ? opportunity?.screeningQuestion 
    : `${opportunity.screeningQuestion.slice(0, MAX_LENGTH)}...`;

  const { mutate: apply, isPending } = useApplyForOpportunity();
  const { data: user, isLoading: meLoading } = useMe();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (opportunity?.screeningQuestion && !form.screeningAnswer.trim()) {
      setError('Please answer the clinical screening question.');
      return;
    }
    if (uploading) {
      setError('Please wait for your files to finish uploading.');
      return;
    }
    if (resume?.failed || caseResponse?.failed) {
      setError('A file failed to upload. Please remove it and try again.');
      return;
    }
    setError('');

    // The files are already in B2 — these are the keys their uploads returned.
    // Both are private, so nothing here is a fetchable URL.
    apply(
      {
        opportunityId: opportunity.id,
        data: {
          screeningAnswer: form.screeningAnswer.trim() || undefined,
          resumeUrl: resume?.key || undefined,
          caseResponseUrl: caseResponse?.key || undefined,
        },
      },
      {
        onSuccess: () => {
          onApplySuccess && onApplySuccess(opportunity);
          handleClose();
        },
        onError: (err) => {
          setError(err?.response?.data?.message || 'Failed to submit application. Please try again.');
        },
      }
    );
  };

  const handleClose = () => {
    setForm({ screeningAnswer: '' });
    setResume(null);
    setCaseResponse(null);
    setError('');
    onClose();
  };

  if (!isOpen || !opportunity) return null;

  const isSubmitting = uploading || isPending;

  return (
    <div className="w-full max-w-4xl mx-auto py-2">
      {/* Back button */}
      <button
        onClick={handleClose}
        className="flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-gray-900 mb-6 transition-colors"
      >
        <ArrowLeft size={16} /> Back to Opportunities
      </button>

      {/* Page heading */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Apply for Position</h1>
        <p className="text-sm text-gray-500">
          Complete the application form below and respond to the clinical screening question
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">

        {/* ── Job Summary Card ──────────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-purple-100 shadow-[0_4px_20px_rgba(238,242,255,0.4)] p-8">
          <div className="flex justify-between items-start mb-6 border-b border-gray-100 pb-6">
            <div>
              <h2 className="text-2xl font-extrabold text-gray-900 mb-2">{opportunity.title}</h2>
              <div className="flex items-center gap-6 text-sm text-gray-500 font-medium">
                <span className="flex items-center gap-2">
                  <Building2 size={16} className="text-gray-400" /> {opportunity.clinicName}
                </span>
                <span className="flex items-center gap-2">
                  <MapPin size={16} className="text-gray-400" /> {opportunity.location}
                </span>
              </div>
            </div>
            <span className="bg-[#2563eb] text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-sm whitespace-nowrap">
              {opportunity.type}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs text-gray-400 font-semibold mb-1 uppercase tracking-wider">Experience Required</div>
              <div className="font-bold text-gray-900">{opportunity.experienceRequired}</div>
            </div>
            <div>
              <div className="text-xs text-gray-400 font-semibold mb-1 uppercase tracking-wider">Total Applicants</div>
              <div className="font-bold text-gray-900">{opportunity._count?.applications ?? 0} candidates</div>
            </div>
          </div>
        </div>

        {/* ── Your Information Card ─────────────────────────────── */}
        <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center gap-2 text-sm font-bold text-gray-900 mb-5">
            <FileText size={18} className="text-[#3b82f6]" />
            Your Information
            <span className="text-gray-400 font-normal ml-2">• From your profile</span>
          </div>

          {meLoading ? (
            <InfoSkeleton />
          ) : (() => {
            const isStudent = user?.role === 'student';
            const specialtyVal = user?.specialization || user?.specialty || user?.jobRole || 'General Practitioner';
            const experienceVal = user?.yearsOfExperience !== undefined && user?.yearsOfExperience !== null && user?.yearsOfExperience !== ''
              ? `${user.yearsOfExperience} years`
              : (user?.experience || '—');

            return (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <div className="text-xs text-gray-400 font-semibold mb-1 uppercase">Name</div>
                  <div className="text-sm font-bold text-gray-900">{user?.name ?? '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-400 font-semibold mb-1 uppercase">Email</div>
                  <div className="text-sm font-bold text-gray-900 break-all">{user?.email ?? '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-400 font-semibold mb-1 uppercase">Phone</div>
                  <div className="text-sm font-bold text-gray-900">{user?.phone ?? '—'}</div>
                </div>
                <div className="sm:col-span-2 lg:col-span-2">
                  <div className="text-xs text-gray-400 font-semibold mb-1 uppercase">
                    {isStudent ? 'Veterinary College' : 'Specialty'}
                  </div>
                  <div className="text-sm font-bold text-gray-900">
                    {isStudent ? (user?.studentProfile?.college?.name ?? '—') : specialtyVal}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-400 font-semibold mb-1 uppercase">
                    {isStudent ? 'Year' : 'Experience'}
                  </div>
                  <div className="text-sm font-bold text-gray-900">
                    {isStudent 
                      ? (user?.studentProfile?.yearOfStudy ? `Year ${user.studentProfile.yearOfStudy}` : '—') 
                      : experienceVal
                    }
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* ── Clinical Screening Question Card ─────────────────── */}
        <div className="bg-white rounded-2xl border border-purple-200 p-8 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center border border-purple-100">
              <Stethoscope size={20} className="text-purple-600" />
            </div>
            <div>
              <h3 className="text-lg font-extrabold text-gray-900">Clinical Screening Question</h3>
            </div>
          </div>

          {opportunity.screeningQuestion ? (
            <>
              <div className="bg-purple-50/30 border border-purple-100/60 rounded-xl p-6 mb-6">
                <div className="text-xs font-bold text-purple-700 tracking-wider mb-3">QUESTION:</div>
                <p 
                  className="text-gray-900 font-medium leading-relaxed whitespace-pre-wrap"
                  onClick={() => isLongQuestion && setIsExpanded(!isExpanded)}
                  style={{ cursor: isLongQuestion ? 'pointer' : 'default' }}
                >
                  {displayQuestion}
                </p>
                {isLongQuestion && (
                  <button
                    type="button"
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="text-[#6b46c1] font-bold text-xs mt-2 hover:underline focus:outline-none"
                  >
                    {isExpanded ? 'Show less' : 'Read more'}
                  </button>
                )}

                {/* Attached Clinical Case Image / Document */}
                {opportunity?.caseAttachmentUrl && (
                  <div className="mt-4 pt-3 border-t border-purple-100/80">
                    <div className="text-xs font-semibold text-purple-700 mb-2 flex items-center gap-1.5">
                      <ImageIcon size={14} /> Attached Clinical Case
                    </div>
                    {/\.(jpg|jpeg|png|webp|gif)(\?.*)?$/i.test(opportunity.caseAttachmentUrl) ? (
                      <div className="relative group rounded-xl overflow-hidden border border-purple-200 bg-white max-h-72 max-w-md">
                        <img 
                          src={opportunity.caseAttachmentUrl} 
                          alt="Attached Clinical Case" 
                          className="w-full h-full object-cover max-h-72 cursor-pointer transition-transform duration-300 group-hover:scale-105" 
                          onClick={() => window.open(opportunity.caseAttachmentUrl, '_blank')}
                        />
                      </div>
                    ) : (
                      <a 
                        href={opportunity.caseAttachmentUrl} 
                        target="_blank" 
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 text-xs font-semibold text-purple-700 bg-white px-3.5 py-2 rounded-lg border border-purple-200 hover:bg-purple-50 transition-colors shadow-xs"
                      >
                        <FileText size={16} /> View Case Attachment ↗
                      </a>
                    )}
                  </div>
                )}
              </div>

              <div className="mb-2 text-sm font-bold text-gray-900">
                Your Clinical Response <span className="text-red-500">*</span>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                Provide a detailed response demonstrating your clinical reasoning, diagnostic approach, and treatment plan.
              </p>
              <textarea
                maxLength={1000}
                value={form.screeningAnswer}
                onChange={(e) => { 
                  const val = e.target.value.slice(0, 1000);
                  setForm(p => ({ ...p, screeningAnswer: val })); 
                  setError(''); 
                }}
                placeholder={`Begin your clinical analysis here...\n\nInclude:\n• Differential diagnoses\n• Diagnostic workup plan\n• Treatment approach\n• Clinical reasoning`}
                className="w-full h-64 p-4 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none text-sm leading-relaxed mb-2 font-medium break-words [overflow-wrap:anywhere]"
              />
              <div className="flex justify-between items-center text-xs text-gray-400 mb-4">
                <span>Maximum 1,000 characters</span>
                <span className={form.screeningAnswer.length >= 950 ? 'text-amber-600 font-semibold' : ''}>
                  {form.screeningAnswer.length} / 1,000 characters
                </span>
              </div>

              <div className="bg-[#eff6ff] border border-blue-100 rounded-xl p-4 flex items-start gap-3">
                <span className="text-lg">💡</span>
                <p className="text-sm text-blue-800 leading-relaxed font-medium">
                  <span className="font-bold">Tip:</span> Strong applications include comprehensive differential
                  diagnoses, evidence-based diagnostic plans, and clear clinical reasoning. Reference similar
                  cases you've studied to demonstrate your knowledge.
                </p>
              </div>
            </>
          ) : (
            <div className="text-sm text-gray-500 italic">
              This position does not have a clinical screening question.
            </div>
          )}
        </div>

        {/* ── Supporting Documents Card ─────────────────────────── */}
        <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <Upload size={20} className="text-blue-500" />
            <h3 className="text-lg font-extrabold text-gray-900">Supporting Documents</h3>
            <span className="bg-gray-100 text-gray-600 text-xs font-semibold px-2 py-0.5 rounded">Optional</span>
          </div>
          <p className="text-sm text-gray-500 mb-6">Strengthen your application with additional materials</p>

          <div className="space-y-6">
            <FileDropZone
              label="Resume / CV"
              accept="PDF, DOC, DOCX • Up to 1MB"
              icon={Upload}
              slot={resume}
              onFileSelect={(f) => startUpload(f, 'resume', setResume)}
              onClear={() => setResume(null)}
              onError={setError}
            />
            <FileDropZone
              label="Previous Case Responses"
              accept="PDF, DOC, DOCX, JPG, PNG • Up to 1MB"
              icon={Stethoscope}
              slot={caseResponse}
              onFileSelect={(f) => startUpload(f, 'case-response', setCaseResponse)}
              onClear={() => setCaseResponse(null)}
              onError={setError}
            />
          </div>

          {/* Only counts files that finished uploading — a file still in flight
              or one that failed is not attached to anything yet. */}
          {(resume?.key || caseResponse?.key) && (
            <div className="mt-4 flex items-center gap-2 text-xs text-green-600 font-semibold">
              <CheckCircle size={14} />
              {[resume?.key && 'Resume', caseResponse?.key && 'Case response'].filter(Boolean).join(' and ')} uploaded
            </div>
          )}
        </div>

        {error && (
          <div className="p-4 bg-red-50 text-red-600 rounded-xl border border-red-100 text-sm">{error}</div>
        )}

        {/* ── Footer / Submit ───────────────────────────────────── */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between shadow-sm">
          <div className="mb-4 sm:mb-0 max-w-xl">
            <h4 className="text-gray-900 font-bold mb-1">Ready to submit?</h4>
            <p className="text-xs text-gray-500">
              Ensure you've provided a comprehensive response to the clinical screening question demonstrating your diagnostic reasoning.
            </p>
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex items-center justify-center gap-2 px-8 py-3 bg-[#818cf8] hover:bg-[#6366f1] text-white font-bold rounded-xl transition-colors shadow-sm disabled:opacity-70 whitespace-nowrap"
          >
            {/* Spins for the submit only. `isSubmitting` folds in `uploading`,
                and using it here would add a second spinner next to the one
                each file already shows while it transfers. Disabling still uses
                `isSubmitting`, so the form cannot be sent mid-upload. */}
            {isPending ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send size={16} /> Submit Application
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ApplyModal;
